import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import {
  addMonths,
  format,
  parse,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  getDay,
  isSameDay,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import { ConfigProvider, Button, Segmented, Dropdown, Popover } from 'antd';
import { CalendarOutlined, PlusOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'antd/dist/reset.css';
import './ScheduleCalendar.less';
import { ScheduleAddPlusIcon } from '../Common/Icons';
import { CALENDAR_WEEK_START_INDEX } from '@/constants/calendar';

dayjs.extend(utc);
dayjs.extend(timezone);

/* ---- localizer ---- */
const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: CALENDAR_WEEK_START_INDEX }),
  getDay,
  locales,
});

/* ---- cap events per day (month view only) ---- */
const MAX_PER_DAY = 3;
const DEFAULT_SCROLL_HOUR = 0;
const NAVIGATION_MONTH_LIMIT = 3;

const toDayStart = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getNavigationBounds = (baseDate) => {
  const base = toDayStart(baseDate);
  return {
    minDate: addMonths(base, -NAVIGATION_MONTH_LIMIT),
    maxDate: addMonths(base, NAVIGATION_MONTH_LIMIT),
  };
};

const clampToBounds = (date, minDate, maxDate) => {
  if (date < minDate) return minDate;
  if (date > maxDate) return maxDate;
  return date;
};

const getNextNavigationDate = (currentDate, view, action, todayDate) => {
  if (action === 'TODAY') {
    return toDayStart(todayDate);
  }

  if (action === 'PREV') {
    if (view === 'day') {
      return new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate() - 1,
        0,
        0,
        0,
        0
      );
    }
    if (view === 'week') {
      return new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate() - 7,
        0,
        0,
        0,
        0
      );
    }
    return new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1, 0, 0, 0, 0);
  }

  if (action === 'NEXT') {
    if (view === 'day') {
      return new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate() + 1,
        0,
        0,
        0,
        0
      );
    }
    if (view === 'week') {
      return new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate() + 7,
        0,
        0,
        0,
        0
      );
    }
    return new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1, 0, 0, 0, 0);
  }

  return currentDate;
};

function capEvents(events, maxPerDay) {
  const byDay = new Map();
  for (const ev of events) {
    const key = format(ev.start, 'yyyy-MM-dd');
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(ev);
  }
  const out = [];
  for (const [, list] of byDay) {
    list.sort((a, b) => a.start - b.start);
    const shown = list.slice(0, maxPerDay);
    shown.forEach((ev) => {
      out.push({
        ...ev,
        __originalEvent: ev,
      });
    });
    const hidden = list.length - shown.length;
    if (hidden > 0) {
      const d = shown[0].start;
      out.push({
        id: `more-${format(d, 'yyyy-MM-dd')}`,
        title: `+${hidden} more`,
        start: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 58),
        end: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59),
        __more: hidden,
        __allEvents: list,
      });
    }
  }
  return out;
}

function capTimeGridEvents(events, maxPerBlock, is24Hours) {
  const BLOCK_EVENT_STEP_MINUTES = 18;
  const BLOCK_EVENT_DURATION_MINUTES = 10;
  const BLOCK_MORE_START_MINUTES = 50;
  const BLOCK_MORE_END_MINUTES = 59;

  const byBlock = new Map();

  for (const ev of events) {
    const blockStart = new Date(
      ev.start.getFullYear(),
      ev.start.getMonth(),
      ev.start.getDate(),
      ev.start.getHours(),
      0,
      0,
      0
    );
    const key = format(blockStart, 'yyyy-MM-dd-HH');
    if (!byBlock.has(key)) {
      byBlock.set(key, { blockStart, list: [] });
    }
    byBlock.get(key).list.push(ev);
  }

  const out = [];

  for (const [, block] of byBlock) {
    block.list.sort((a, b) => a.start - b.start);
    const shown = block.list.slice(0, maxPerBlock);

    shown.forEach((ev, idx) => {
      const start = new Date(
        block.blockStart.getTime() + idx * BLOCK_EVENT_STEP_MINUTES * 60 * 1000
      );
      const end = new Date(
        block.blockStart.getTime() +
          (idx * BLOCK_EVENT_STEP_MINUTES + BLOCK_EVENT_DURATION_MINUTES) * 60 * 1000
      );
      out.push({
        ...ev,
        time: ev.time || format(ev.start, is24Hours ? 'HH:mm' : 'h:mm a'),
        start,
        end,
        __originalEvent: ev,
      });
    });

    const hidden = block.list.length - shown.length;
    if (hidden > 0) {
      out.push({
        id: `more-${format(block.blockStart, 'yyyy-MM-dd-HH')}`,
        title: `+${hidden} more`,
        start: new Date(block.blockStart.getTime() + BLOCK_MORE_START_MINUTES * 60 * 1000),
        end: new Date(block.blockStart.getTime() + BLOCK_MORE_END_MINUTES * 60 * 1000),
        __more: hidden,
        __allEvents: block.list,
      });
    }
  }

  out.sort((a, b) => a.start - b.start);
  return out;
}

/* ---- chip components ---- */
const ChipInner = ({ event, onClick, is24Hours = false }) => {
  const timeStr =
    event.time ||
    event.start?.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: !is24Hours,
    });
  const typeClass = event.eventType
    ? `sc-chip--${event.eventType === 'zoom' ? 'schedule' : event.eventType}`
    : '';
  return (
    <button
      type="button"
      className={`sc-chip ${typeClass}`}
      title={`${event.title} · ${timeStr}`}
      onClick={(clickEvent) => onClick?.(event, clickEvent)}
    >
      <div className="sc-chip__top">
        <span className="sc-chip__icon">{event.iconEl}</span>
        <span className="sc-chip__name">{event.title}</span>
      </div>
      <span className="sc-chip__time">{timeStr}</span>
    </button>
  );
};

ChipInner.propTypes = {
  event: PropTypes.shape({
    iconEl: PropTypes.element,
    eventType: PropTypes.string,
    title: PropTypes.string,
    time: PropTypes.string,
    start: PropTypes.instanceOf(Date),
    __more: PropTypes.number,
    __allEvents: PropTypes.array,
  }).isRequired,
  onClick: PropTypes.func,
  is24Hours: PropTypes.bool,
};

const SkeletonChip = () => <div className="sc-chip-skeleton" />;

const EventComponent = ({ event, onSelectEvent, is24Hours }) => {
  if (event.__skeleton) {
    return <SkeletonChip />;
  }
  if (event.__more) {
    return (
      <Popover
        trigger="click"
        placement="top"
        overlayClassName="sc-more-popover"
        content={
          <div className="sc-pop">
            {event.__allEvents.map((ev) => (
              <ChipInner key={ev.id} event={ev} onClick={onSelectEvent} is24Hours={is24Hours} />
            ))}
          </div>
        }
      >
        <button
          type="button"
          className="sc-more"
          onClick={(clickEvent) => {
            clickEvent.preventDefault();
            clickEvent.stopPropagation();
          }}
          onMouseDown={(mouseEvent) => {
            mouseEvent.preventDefault();
            mouseEvent.stopPropagation();
          }}
        >
          +{event.__more} more
        </button>
      </Popover>
    );
  }
  return <ChipInner event={event} is24Hours={is24Hours} />;
};

EventComponent.propTypes = {
  event: PropTypes.object.isRequired,
  onSelectEvent: PropTypes.func,
  is24Hours: PropTypes.bool,
};

function TimeGridHeader({ date, today }) {
  return (
    <div className={isSameDay(date, today) ? 'sc-time-head is-today' : 'sc-time-head'}>
      <span className="sc-time-head__day">{format(date, 'EEEE')}</span>
      <span className="sc-time-head__date">{format(date, 'd')}</span>
    </div>
  );
}

TimeGridHeader.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  today: PropTypes.instanceOf(Date).isRequired,
};

/* ---- toolbar ---- */
const CustomToolbar = ({ label, onNavigate, onView, view, disablePrev, disableNext }) => {
  const navClassName = [
    'sc-nav',
    disablePrev ? 'sc-nav--prev-disabled' : '',
    disableNext ? 'sc-nav--next-disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="sc-nav-row">
      <span style={{ width: 200 }} />
      <div className={navClassName}>
        <Button
          type="text"
          shape="circle"
          icon={<LeftOutlined />}
          onClick={() => onNavigate('PREV')}
          disabled={disablePrev}
        />
        <span className="sc-nav__label">{label}</span>
        <Button
          type="text"
          shape="circle"
          icon={<RightOutlined />}
          onClick={() => onNavigate('NEXT')}
          disabled={disableNext}
        />
      </div>
      <div className="sc-nav-right">
        <Segmented
          value={view}
          onChange={(v) => onView(v)}
          options={[
            { label: 'Day', value: 'day' },
            { label: 'Week', value: 'week' },
            { label: 'Month', value: 'month' },
          ]}
        />
        <Button icon={<CalendarOutlined />} onClick={() => onNavigate('TODAY')}>
          Today
        </Button>
      </div>
    </div>
  );
};

CustomToolbar.propTypes = {
  label: PropTypes.string.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  view: PropTypes.string.isRequired,
  disablePrev: PropTypes.bool,
  disableNext: PropTypes.bool,
};

/* ---- main component ---- */
const DEFAULT_TODAY = new Date();
const toFloatingDayDate = (value) => {
  if (!value) return value;
  const dateValue = value instanceof Date ? value : new Date(value);
  return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(), 12, 0, 0, 0);
};

const getFloatingDayKey = (value) => {
  if (!value) return '';
  const dateValue = value instanceof Date ? value : new Date(value);
  return `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, '0')}-${String(dateValue.getDate()).padStart(2, '0')}`;
};

const getFloatingDateFromDayKey = (dayKey) => {
  if (!dayKey) return null;
  const [year, month, day] = dayKey.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

export default function ScheduleCalendar({
  events = [],
  defaultDate,
  timezone,
  defaultView = 'day',
  today,
  maxPerDay = MAX_PER_DAY,
  onSelectEvent = () => {},
  onAdd = () => {},
  addDropdownProps,
  onCalendarChange,
  dayViewComponent: DayViewComponent,
  record,
  openSubMenu,
  loading = false,
  filterSlot,
}) {
  const currentUser = useSelector(getCurrentUser);
  const useruuid = useMemo(() => currentUser.useruuid, [currentUser]);
  const userData = useSelector((user) => user.data.userInfo);
  const updatedUserInfo = userData[useruuid];
  const is24Hours = useMemo(() => updatedUserInfo?.is24hoursformat, [updatedUserInfo]);
  const resolvedToday = useMemo(() => {
    if (today) return toFloatingDayDate(today);
    if (!timezone) return toFloatingDayDate(new Date());
    return getFloatingDateFromDayKey(dayjs().tz(timezone).format('YYYY-MM-DD'));
  }, [today, timezone]);

  const { minDate, maxDate } = useMemo(() => getNavigationBounds(resolvedToday), [resolvedToday]);

  const resolvedDefaultDate = useMemo(() => {
    const sourceDate = defaultDate ?? resolvedToday;
    return toFloatingDayDate(sourceDate);
  }, [defaultDate, resolvedToday, timezone]);

  const [viewDate, setViewDate] = useState(() =>
    clampToBounds(toFloatingDayDate(resolvedDefaultDate), minDate, maxDate)
  );
  const [curView, setCurView] = useState(defaultView ?? 'day');
  const calendarPanelRef = useRef(null);

  const getViewRange = useCallback((date, v) => {
    if (v === 'day') {
      return {
        start: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0),
        end: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59),
      };
    }
    if (v === 'week') {
      return {
        start: startOfWeek(date, { weekStartsOn: CALENDAR_WEEK_START_INDEX }),
        end: endOfWeek(date, { weekStartsOn: CALENDAR_WEEK_START_INDEX }),
      };
    }
    return {
      start: startOfWeek(startOfMonth(date), { weekStartsOn: CALENDAR_WEEK_START_INDEX }),
      end: endOfWeek(endOfMonth(date), { weekStartsOn: CALENDAR_WEEK_START_INDEX }),
    };
  }, []);

  const onCalendarChangeRef = useRef(onCalendarChange);
  useEffect(() => {
    onCalendarChangeRef.current = onCalendarChange;
  });

  useEffect(() => {
    if (onCalendarChangeRef.current) {
      const { start, end } = getViewRange(viewDate, curView);
      onCalendarChangeRef.current({ start, end, view: curView, date: viewDate });
    }
  }, [viewDate, curView, getViewRange]);

  const dayViewEvents = useMemo(
    () => events.filter((ev) => isSameDay(ev.start, viewDate)),
    [events, viewDate]
  );

  const handleViewChange = (nextView) => {
    setCurView(nextView);
  };

  const renderDayView = () => {
    if (!DayViewComponent) return null;

    if (React.isValidElement(DayViewComponent)) {
      return DayViewComponent;
    }

    if (typeof DayViewComponent === 'function') {
      // Call as render function to avoid remounting when the function reference changes
      return DayViewComponent({ date: viewDate, events: dayViewEvents, onSelectEvent });
    }

    return DayViewComponent;
  };

  const skeletonEvents = useMemo(() => {
    if (!loading) return [];
    const ms = startOfMonth(viewDate);
    const daysInMonth = endOfMonth(viewDate).getDate();
    const pattern = [1, 2, 1, 2, 1, 3, 1];
    const result = [];
    let id = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const count = pattern[day % pattern.length];
      for (let i = 0; i < count; i++) {
        result.push({
          id: `skeleton-${id++}`,
          title: '',
          start: new Date(ms.getFullYear(), ms.getMonth(), day, 8 + i * 2, 0),
          end: new Date(ms.getFullYear(), ms.getMonth(), day, 9 + i * 2, 0),
          __skeleton: true,
        });
      }
    }
    return result;
  }, [loading, viewDate]);

  const displayEvents = useMemo(() => {
    const source = loading ? skeletonEvents : events;
    if (curView === 'month') {
      return capEvents(source, maxPerDay);
    }
    if (curView === 'week' || curView === 'day') {
      return capTimeGridEvents(source, maxPerDay, is24Hours);
    }
    return source;
  }, [events, maxPerDay, curView, loading, skeletonEvents, is24Hours]);

  const scrollToTime = useMemo(() => {
    const baseDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), viewDate.getDate());

    if (curView !== 'week' && curView !== 'day') {
      return new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate(),
        DEFAULT_SCROLL_HOUR,
        0,
        0,
        0
      );
    }

    const source = loading ? skeletonEvents : events;
    const { start, end } = getViewRange(viewDate, curView);

    let firstEventStart = null;
    for (const ev of source) {
      if (!(ev?.start instanceof Date)) continue;
      if (ev.start < start || ev.start > end) continue;
      if (!firstEventStart || ev.start < firstEventStart) {
        firstEventStart = ev.start;
      }
    }

    if (!firstEventStart) {
      return new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate(),
        DEFAULT_SCROLL_HOUR,
        0,
        0,
        0
      );
    }

    return new Date(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      baseDate.getDate(),
      firstEventStart.getHours(),
      Math.max(0, firstEventStart.getMinutes() - 15),
      0,
      0
    );
  }, [curView, events, getViewRange, loading, skeletonEvents, viewDate]);

  const calendarRenderKey = useMemo(() => {
    if (curView === 'week' || curView === 'day') {
      const { start } = getViewRange(viewDate, curView);
      return `sc-${curView}-${format(start, 'yyyy-MM-dd')}-${format(scrollToTime, 'HH-mm')}`;
    }
    return `sc-${curView}-${format(viewDate, 'yyyy-MM')}`;
  }, [curView, getViewRange, scrollToTime, viewDate]);

  useEffect(() => {
    if (curView !== 'week' && curView !== 'day') return;

    const rafId = requestAnimationFrame(() => {
      const container = calendarPanelRef.current;
      if (!container) return;

      const timeContent = container.querySelector('.rbc-time-content');
      if (!(timeContent instanceof HTMLElement)) return;

      const targetMinutes = scrollToTime.getHours() * 60 + scrollToTime.getMinutes();
      const totalMinutesInDay = 24 * 60;
      const maxScrollableTop = Math.max(0, timeContent.scrollHeight - timeContent.clientHeight);
      const ratio = targetMinutes / totalMinutesInDay;
      const targetTop = Math.max(0, Math.round(maxScrollableTop * ratio) - 20);

      timeContent.scrollTop = targetTop;

      if (timeContent.scrollTop === 0) {
        const firstTimedEvent = container.querySelector('.rbc-time-view .rbc-event');
        if (firstTimedEvent instanceof HTMLElement) {
          firstTimedEvent.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
        }
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [calendarRenderKey, curView, scrollToTime]);

  const handleSelectEvent = useCallback(
    (event, clickEvent) => {
      if (event.__skeleton || event.__more) {
        clickEvent?.preventDefault?.();
        clickEvent?.stopPropagation?.();
        return;
      }
      onSelectEvent(event.__originalEvent || event, clickEvent);
    },
    [onSelectEvent]
  );

  const components = useMemo(
    () => ({
      event: (props) => (
        <EventComponent {...props} onSelectEvent={handleSelectEvent} is24Hours={is24Hours} />
      ),
      week: {
        header: ({ date }) => <TimeGridHeader date={date} today={resolvedToday} />,
      },
      day: {
        header: ({ date }) => <TimeGridHeader date={date} today={resolvedToday} />,
      },
      month: {
        dateHeader: ({ date, label }) => {
          const isTd = isSameDay(date, resolvedToday);
          return (
            <div className="sc-date-head">
              <span className={isTd ? 'sc-num is-today' : 'sc-num'}>{label}</span>
              {isTd && <span className="sc-today-tag">Today</span>}
            </div>
          );
        },
      },
    }),
    [resolvedToday, handleSelectEvent, is24Hours]
  );

  const handleNavigate = (action) => {
    const nextDate = getNextNavigationDate(viewDate, curView, action, resolvedToday);
    setViewDate(clampToBounds(toFloatingDayDate(nextDate), minDate, maxDate));
  };

  const disablePrev = useMemo(() => {
    const nextDate = getNextNavigationDate(viewDate, curView, 'PREV', resolvedToday);
    const clamped = clampToBounds(nextDate, minDate, maxDate);
    return clamped.getTime() === toDayStart(viewDate).getTime();
  }, [viewDate, curView, resolvedToday, minDate, maxDate]);

  const disableNext = useMemo(() => {
    const nextDate = getNextNavigationDate(viewDate, curView, 'NEXT', resolvedToday);
    const clamped = clampToBounds(nextDate, minDate, maxDate);
    return clamped.getTime() === toDayStart(viewDate).getTime();
  }, [viewDate, curView, resolvedToday, minDate, maxDate]);

  const navLabel = useMemo(() => {
    if (curView === 'day') return format(viewDate, 'EEEE MMMM dd');
    if (curView === 'week') {
      const start = startOfWeek(viewDate, { weekStartsOn: CALENDAR_WEEK_START_INDEX });
      const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
      return `${format(start, 'MMM dd')} – ${format(end, 'MMM dd')}`;
    }
    return format(viewDate, 'MMMM yyyy');
  }, [viewDate, curView]);

  const calendarFormats = useMemo(
    () => ({
      timeGutterFormat: (date) => format(date, is24Hours ? 'HH:mm' : 'h a'),
    }),
    [is24Hours]
  );

  const eventPropGetter = useCallback(
    (event) => {
      if (curView !== 'month' || !event?.start) return {};
      const sameMonth =
        event.start.getMonth() === viewDate.getMonth() &&
        event.start.getFullYear() === viewDate.getFullYear();
      return sameMonth ? {} : { className: 'sc-event--off-range' };
    },
    [curView, viewDate]
  );

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: '#2563eb', borderRadius: 9 },
        components: { Segmented: { itemSelectedBg: '#dbeafe', itemSelectedColor: '#2563eb' } },
      }}
    >
      <div className="sc-wrap">
        <div className="sc-top">
          <div className="sc-top-actions">
            {addDropdownProps ? (
              <Dropdown {...addDropdownProps}>
                <a onClick={(e) => e.preventDefault()}>
                  {openSubMenu && record?.rolename !== '' && <ScheduleAddPlusIcon />}
                </a>
              </Dropdown>
            ) : (
              <Button className="sc-add-btn" icon={<PlusOutlined />} onClick={onAdd}>
                Add
              </Button>
            )}
            {filterSlot ? <div className="sc-filter-slot">{filterSlot}</div> : null}
          </div>
        </div>

        <div className="sc-header-sticky">
          <CustomToolbar
            label={navLabel}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            view={curView}
            disablePrev={disablePrev}
            disableNext={disableNext}
          />
        </div>

        {curView === 'day' && DayViewComponent ? (
          <div className="sc-panel sc-panel--day">{renderDayView()}</div>
        ) : (
          <div className="sc-panel" ref={calendarPanelRef}>
            <Calendar
              key={calendarRenderKey}
              localizer={localizer}
              events={displayEvents}
              formats={calendarFormats}
              view={curView}
              onView={handleViewChange}
              date={viewDate}
              onNavigate={(d) => setViewDate(clampToBounds(toFloatingDayDate(d), minDate, maxDate))}
              startAccessor="start"
              endAccessor="end"
              views={['month', 'week', 'day']}
              components={components}
              toolbar={false}
              showAllEvents
              popup={false}
              onSelectEvent={handleSelectEvent}
              eventPropGetter={eventPropGetter}
              scrollToTime={scrollToTime}
              enableAutoScroll
              style={{ minHeight: 680 }}
            />
          </div>
        )}
      </div>
    </ConfigProvider>
  );
}

ScheduleCalendar.propTypes = {
  events: PropTypes.array,
  defaultDate: PropTypes.instanceOf(Date),
  timezone: PropTypes.string,
  defaultView: PropTypes.oneOf(['day', 'week', 'month']),
  today: PropTypes.instanceOf(Date),
  maxPerDay: PropTypes.number,
  onSelectEvent: PropTypes.func,
  onAdd: PropTypes.func,
  addDropdownProps: PropTypes.object,
  onCalendarChange: PropTypes.func,
  dayViewComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  record: PropTypes.object,
  openSubMenu: PropTypes.bool,
  loading: PropTypes.bool,
  filterSlot: PropTypes.node,
};
